import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('subject', formData.subject);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d4nfb4hbtbscp07qbcm0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen bg-white font-['Inter',sans-serif]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/90 backdrop-blur-md shadow-sm">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <div className="flex items-center">
              <img 
                src="https://public.readdy.ai/ai/img_res/1f125a2c-5554-4544-8642-02f7b52bf825.png" 
                alt="CONFECCIONES ASTRID IBAGUE SAS" 
                className="h-12 w-auto object-contain"
              />
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('home')} className="text-gray-700 hover:text-teal-500 transition-colors font-medium cursor-pointer whitespace-nowrap">Home</button>
              <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-teal-500 transition-colors font-medium cursor-pointer whitespace-nowrap">About</button>
              <button onClick={() => scrollToSection('product')} className="text-gray-700 hover:text-teal-500 transition-colors font-medium cursor-pointer whitespace-nowrap">VPN Product</button>
              <button onClick={() => scrollToSection('security')} className="text-gray-700 hover:text-teal-500 transition-colors font-medium cursor-pointer whitespace-nowrap">Security</button>
              <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-teal-500 transition-colors font-medium cursor-pointer whitespace-nowrap">Contact</button>
            </div>

            {/* CTA Button */}
            <div className="hidden md:block">
              <button 
                onClick={() => scrollToSection('product')}
                className="px-6 py-3 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-full font-semibold hover:shadow-lg hover:shadow-teal-500/50 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                Get Started
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden text-gray-700 cursor-pointer"
            >
              <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-2xl`}></i>
            </button>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-200">
              <div className="flex flex-col space-y-4">
                <button onClick={() => scrollToSection('home')} className="text-gray-700 hover:text-teal-500 transition-colors font-medium text-left cursor-pointer">Home</button>
                <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-teal-500 transition-colors font-medium text-left cursor-pointer">About</button>
                <button onClick={() => scrollToSection('product')} className="text-gray-700 hover:text-teal-500 transition-colors font-medium text-left cursor-pointer">VPN Product</button>
                <button onClick={() => scrollToSection('security')} className="text-gray-700 hover:text-teal-500 transition-colors font-medium text-left cursor-pointer">Security</button>
                <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-teal-500 transition-colors font-medium text-left cursor-pointer">Contact</button>
                <button 
                  onClick={() => scrollToSection('product')}
                  className="px-6 py-3 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-full font-semibold text-center cursor-pointer whitespace-nowrap"
                >
                  Get Started
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900">
          <img 
            src="https://public.readdy.ai/ai/img_res/c7330100a1cee4ee772a66d58eae7654.jpg"
            alt="Network Security Background"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-white">
              <h1 className="text-6xl lg:text-7xl font-bold leading-tight mb-6">
                Your Digital<br />
                Privacy<br />
                <span className="text-teal-400">Fortress</span>
              </h1>
              <p className="text-lg text-gray-300 mb-8 leading-relaxed max-w-xl">
                Experience unparalleled online security with military-grade encryption. ASTRID VPN protects your digital freedom, ensuring your data remains private and your connection stays secure across Colombia and beyond.
              </p>
              <button 
                onClick={() => scrollToSection('product')}
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-full font-semibold text-lg hover:shadow-2xl hover:shadow-teal-500/50 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                Secure Your Connection
                <i className="ri-arrow-right-line ml-2 text-xl"></i>
              </button>
            </div>

            {/* Right Visual */}
            <div className="hidden lg:block">
              <div className="relative">
                <img 
                  src="https://readdy.ai/api/search-image?query=3D%20holographic%20shield%20with%20glowing%20cyan%20edges%20floating%20in%20space%2C%20digital%20security%20protection%20symbol%2C%20futuristic%20encryption%20layers%20visualization%2C%20geometric%20shapes%20and%20particles%2C%20transparent%20glass%20effect%20with%20gradient%20colors%2C%20high-tech%20cybersecurity%20illustration%20on%20dark%20background&width=800&height=800&seq=hero-shield-001&orientation=squarish"
                  alt="Digital Security Shield"
                  className="w-full h-auto object-contain drop-shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-5 gap-16 items-center">
            {/* Left Content */}
            <div className="lg:col-span-2">
              <div className="inline-block px-4 py-2 bg-teal-100 text-teal-600 rounded-full text-xs font-bold tracking-wider mb-6">
                WHO WE ARE
              </div>
              <h2 className="text-5xl font-bold text-gray-900 mb-6 leading-tight font-['Playfair_Display',serif]">
                Protecting Digital Freedom Since Day One
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  CONFECCIONES ASTRID IBAGUE SAS is a pioneering technology company based in Ibagué, Colombia, dedicated to safeguarding digital privacy and online security for users worldwide. Our mission is to make secure internet access accessible to everyone.
                </p>
                <p>
                  We believe that privacy is a fundamental right, not a luxury. That's why we've developed ASTRID VPN with cutting-edge encryption technology and a strict zero-log policy, ensuring your online activities remain completely private and secure.
                </p>
                <p>
                  Our team of cybersecurity experts works tirelessly to provide you with the most reliable VPN service, combining advanced security features with lightning-fast connection speeds. From Ibagué to the world, we're committed to protecting your digital life.
                </p>
              </div>
            </div>

            {/* Right Visual */}
            <div className="lg:col-span-3">
              <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-teal-50 to-cyan-50 p-12">
                <img 
                  src="https://readdy.ai/api/search-image?query=Global%20network%20map%20with%20Colombia%20highlighted%20in%20cyan%20color%2C%20world%20connections%20with%20glowing%20lines%20and%20data%20points%2C%20digital%20world%20map%20visualization%2C%20modern%20tech%20illustration%20with%20floating%20network%20nodes%2C%20clean%20minimalist%20design%20on%20light%20gradient%20background&width=900&height=600&seq=about-map-001&orientation=landscape"
                  alt="Global Network"
                  className="w-full h-auto object-contain"
                />
                <div className="absolute bottom-8 right-8 bg-white rounded-2xl shadow-xl p-6 flex items-center space-x-4">
                  <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center">
                    <i className="ri-map-pin-line text-white text-2xl"></i>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 font-medium">Based in</p>
                    <p className="text-lg font-bold text-gray-900">Ibagué, Colombia</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Value Pillars */}
          <div className="mt-20 bg-gray-50 rounded-3xl p-12">
            <div className="grid md:grid-cols-3 gap-12">
              <div className="text-center">
                <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-shield-check-line text-white text-3xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Uncompromising Security</h3>
                <p className="text-gray-600">Military-grade encryption protecting your data at all times</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-eye-off-line text-white text-3xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Zero-Log Policy</h3>
                <p className="text-gray-600">We never track, collect, or share your personal data</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-speed-line text-white text-3xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Lightning Fast</h3>
                <p className="text-gray-600">Optimized servers for seamless browsing and streaming</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VPN Product Section */}
      <section id="product" className="py-24 bg-gradient-to-br from-white via-teal-50/30 to-cyan-50/50 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div>
              <div className="inline-block px-4 py-2 bg-teal-500 text-white rounded-full text-sm font-bold tracking-wider mb-6">
                ASTRID VPN
              </div>
              <h2 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-8 leading-tight">
                Speed Meets<br />Security
              </h2>
              
              <div className="space-y-6 mb-10">
                <div className="flex items-start space-x-4">
                  <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-white text-sm"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Military-Grade Encryption</h3>
                    <p className="text-gray-600">AES-256 encryption keeps your data completely secure</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-white text-sm"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Zero-Log Policy</h3>
                    <p className="text-gray-600">Your privacy is guaranteed - we never store your activity</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-white text-sm"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Lightning-Fast Servers</h3>
                    <p className="text-gray-600">Optimized global network for maximum speed</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-white text-sm"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">One-Click Connection</h3>
                    <p className="text-gray-600">Simple, intuitive interface for instant protection</p>
                  </div>
                </div>
              </div>

              <button 
                onClick={() => scrollToSection('contact')}
                className="px-8 py-4 bg-gray-900 text-white rounded-lg font-semibold hover:bg-gray-800 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                Download Now
              </button>
            </div>

            {/* Right Visual */}
            <div className="relative">
              <div className="relative transform lg:rotate-3 hover:rotate-0 transition-transform duration-500">
                <img 
                  src="https://readdy.ai/api/search-image?query=Modern%20VPN%20application%20interface%20mockup%20showing%20connection%20dashboard%20with%20large%20connect%20button%2C%20server%20location%20selector%2C%20connection%20speed%20indicator%2C%20and%20encryption%20status%20display%2C%20clean%20UI%20design%20with%20teal%20and%20cyan%20accents%2C%20floating%20interface%20elements%20around%20main%20screen%2C%20professional%20software%20interface%20on%20light%20gradient%20background&width=800&height=900&seq=vpn-app-001&orientation=portrait"
                  alt="ASTRID VPN Application"
                  className="w-full h-auto object-contain drop-shadow-2xl rounded-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security Features Section */}
      <section id="security" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-teal-100 text-teal-600 rounded-full text-xs font-bold tracking-wider mb-4">
              SECURITY FEATURES
            </div>
            <h2 className="text-5xl font-bold text-gray-900 mb-4">
              Bank-Level Protection for Your Data
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our comprehensive security suite ensures your online activities remain private and protected at all times
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-2 gap-8">
            {/* Feature 1 */}
            <div className="bg-white rounded-3xl p-10 hover:shadow-xl transition-all duration-300 group cursor-pointer">
              <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-full flex items-center justify-center mb-6">
                <i className="ri-lock-line text-white text-4xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">AES-256 Encryption</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                The same encryption standard used by governments and financial institutions worldwide. Your data is protected by virtually unbreakable encryption, ensuring complete privacy for all your online activities.
              </p>
              <span className="inline-flex items-center text-teal-500 font-semibold opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                Learn More <i className="ri-arrow-right-line ml-2"></i>
              </span>
            </div>

            {/* Feature 2 */}
            <div className="bg-white rounded-3xl p-10 hover:shadow-xl transition-all duration-300 group cursor-pointer">
              <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-full flex items-center justify-center mb-6">
                <i className="ri-shield-cross-line text-white text-4xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Kill Switch Protection</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Automatically blocks all internet traffic if your VPN connection drops unexpectedly. This critical feature prevents any data leaks, ensuring your real IP address and sensitive information never get exposed.
              </p>
              <span className="inline-flex items-center text-teal-500 font-semibold opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                Learn More <i className="ri-arrow-right-line ml-2"></i>
              </span>
            </div>

            {/* Feature 3 */}
            <div className="bg-white rounded-3xl p-10 hover:shadow-xl transition-all duration-300 group cursor-pointer">
              <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-full flex items-center justify-center mb-6">
                <i className="ri-radar-line text-white text-4xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">DNS Leak Prevention</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Advanced DNS leak protection ensures your browsing queries are always routed through our secure servers. Your ISP and third parties cannot track which websites you visit or monitor your online behavior.
              </p>
              <span className="inline-flex items-center text-teal-500 font-semibold opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                Learn More <i className="ri-arrow-right-line ml-2"></i>
              </span>
            </div>

            {/* Feature 4 */}
            <div className="bg-white rounded-3xl p-10 hover:shadow-xl transition-all duration-300 group cursor-pointer">
              <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-full flex items-center justify-center mb-6">
                <i className="ri-settings-3-line text-white text-4xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Multi-Protocol Support</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Choose from multiple VPN protocols including OpenVPN, IKEv2, and WireGuard. Each protocol is optimized for different use cases, giving you the flexibility to balance speed and security based on your needs.
              </p>
              <span className="inline-flex items-center text-teal-500 font-semibold opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                Learn More <i className="ri-arrow-right-line ml-2"></i>
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section id="support" className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-teal-100 text-teal-600 rounded-full text-xs font-bold tracking-wider mb-4">
              SUPPORT CENTER
            </div>
            <h2 className="text-5xl font-bold text-gray-900 mb-4 font-['Playfair_Display',serif]">
              We're Here to Help
            </h2>
            <p className="text-lg text-gray-600">
              Get assistance whenever you need it with our comprehensive support resources
            </p>
          </div>

          {/* Support Options */}
          <div className="space-y-6 mb-16">
            <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-2xl p-8 flex items-center space-x-6 hover:shadow-lg transition-all duration-300 cursor-pointer">
              <div className="w-24 h-24 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                <i className="ri-book-open-line text-white text-4xl"></i>
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Knowledge Base</h3>
                <p className="text-gray-600 mb-3">
                  Browse our comprehensive guides, tutorials, and FAQs to find quick answers to common questions
                </p>
                <span className="inline-flex items-center text-teal-600 font-semibold cursor-pointer">
                  Browse Articles <i className="ri-arrow-right-line ml-2"></i>
                </span>
              </div>
            </div>

            <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-2xl p-8 flex items-center space-x-6 hover:shadow-lg transition-all duration-300 cursor-pointer">
              <div className="w-24 h-24 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                <i className="ri-customer-service-2-line text-white text-4xl"></i>
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Contact Support</h3>
                <p className="text-gray-600 mb-3">
                  Reach out to our dedicated support team for personalized assistance with any technical issues
                </p>
                <button 
                  onClick={() => scrollToSection('contact')}
                  className="inline-flex items-center text-teal-600 font-semibold cursor-pointer"
                >
                  Get in Touch <i className="ri-arrow-right-line ml-2"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://readdy.ai/api/search-image?query=Abstract%20geometric%20network%20pattern%20with%20connecting%20lines%20and%20nodes%2C%20digital%20technology%20background%2C%20minimalist%20design%20with%20cyan%20and%20blue%20tones%20on%20dark%20background&width=1920&height=1080&seq=contact-bg-001&orientation=landscape"
            alt="Contact Background"
            className="w-full h-full object-cover"
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Left Info Panel */}
            <div className="text-white">
              <div className="inline-block px-4 py-2 bg-teal-500/20 text-teal-400 rounded-full text-xs font-bold tracking-wider mb-6">
                GET IN TOUCH
              </div>
              <h2 className="text-5xl font-bold mb-8 leading-tight">
                Let's Start a<br />Conversation
              </h2>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-white text-xl"></i>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Email</p>
                    <a href="mailto:info@confeccionesastridibaguesas.com" className="text-lg hover:text-teal-400 transition-colors cursor-pointer">
                      info@confeccionesastridibaguesas.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-white text-xl"></i>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Phone</p>
                    <a href="tel:+13134865430" className="text-lg hover:text-teal-400 transition-colors cursor-pointer">
                      +1 3134865430
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-white text-xl"></i>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Address</p>
                    <p className="text-lg">
                      CARRERA 3A N 33 B 03<br />
                      IBAGUE, Colombia<br />
                      730001
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Form */}
            <div className="bg-white rounded-3xl p-10 shadow-2xl" data-readdy-form>
              <form id="contact-form" onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                    Your Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-semibold text-gray-700 mb-2">
                    Subject
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                    placeholder="How can we help?"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all resize-none"
                    placeholder="Tell us more about your inquiry..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-lg font-semibold text-lg hover:shadow-lg hover:shadow-teal-500/50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>

                {submitStatus === 'success' && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                    Thank you! Your message has been sent successfully.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    Sorry, there was an error sending your message. Please try again.
                  </div>
                )}

                <p className="text-xs text-gray-500 text-center">
                  By submitting this form, you agree to our privacy policy
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            {/* Left Column */}
            <div>
              <img 
                src="https://public.readdy.ai/ai/img_res/1f125a2c-5554-4544-8642-02f7b52bf825.png" 
                alt="CONFECCIONES ASTRID IBAGUE SAS" 
                className="h-12 w-auto object-contain mb-4 brightness-0 invert"
              />
              <p className="text-gray-400 mb-6 leading-relaxed">
                Securing your digital life, one connection at a time
              </p>
            </div>

            {/* Middle Column */}
            <div>
              <h4 className="text-sm font-bold uppercase tracking-wider mb-4">Quick Links</h4>
              <div className="space-y-3">
                <button onClick={() => scrollToSection('home')} className="block text-gray-400 hover:text-teal-400 transition-colors cursor-pointer text-left">Home</button>
                <button onClick={() => scrollToSection('about')} className="block text-gray-400 hover:text-teal-400 transition-colors cursor-pointer text-left">About</button>
                <button onClick={() => scrollToSection('product')} className="block text-gray-400 hover:text-teal-400 transition-colors cursor-pointer text-left">VPN Product</button>
                <button onClick={() => scrollToSection('security')} className="block text-gray-400 hover:text-teal-400 transition-colors cursor-pointer text-left">Security</button>
                <button onClick={() => scrollToSection('contact')} className="block text-gray-400 hover:text-teal-400 transition-colors cursor-pointer text-left">Contact</button>
              </div>
            </div>

            {/* Right Column */}
            <div>
              <h4 className="text-sm font-bold uppercase tracking-wider mb-4">Company</h4>
              <p className="text-gray-400 mb-2">CONFECCIONES ASTRID IBAGUE SAS</p>
              <p className="text-gray-400 mb-1">CARRERA 3A N 33 B 03</p>
              <p className="text-gray-400 mb-4">IBAGUE, Colombia 730001</p>
              <a href="tel:+13134865430" className="text-gray-400 hover:text-teal-400 transition-colors cursor-pointer">
                +1 3134865430
              </a>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-8 border-t border-gray-800">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <div className="flex items-center space-x-6">
                <a href="#" className="text-gray-400 hover:text-teal-400 transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill text-xl"></i>
                </a>
                <a href="#" className="text-gray-400 hover:text-teal-400 transition-colors cursor-pointer">
                  <i className="ri-twitter-x-fill text-xl"></i>
                </a>
                <a href="#" className="text-gray-400 hover:text-teal-400 transition-colors cursor-pointer">
                  <i className="ri-facebook-fill text-xl"></i>
                </a>
              </div>
              
              <p className="text-gray-400 text-sm">
                © 2025 CONFECCIONES ASTRID IBAGUE SAS
              </p>
              
              <div className="flex items-center space-x-4 text-sm">
                <a href="#" className="text-gray-400 hover:text-teal-400 transition-colors cursor-pointer">Privacy Policy</a>
                <span className="text-gray-600">•</span>
                <a href="#" className="text-gray-400 hover:text-teal-400 transition-colors cursor-pointer">Terms of Service</a>
              </div>
            </div>
            
            <div className="text-center mt-6">
              <a 
                href="https://readdy.ai/?origin=logo" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-gray-500 hover:text-teal-400 transition-colors text-xs cursor-pointer"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
